import { type SupplierLedgerRow, type SupplierRefundRow } from '@utazzo/shared';
import { UzButton, UzCard, UzInput } from '@utazzo/ui';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

import { FyRangeControls, useFyRange } from '../components/FyRangeControls';
import { ReceiveRefundDialog } from '../components/ReceiveRefundDialog';
import { getSupplierLedger } from '../invoices.api';
import { formatDateDMY, formatInrPaise } from '../invoices.helpers';
import { useSupplierLedger } from '../use-invoices';

import { PageShell } from '@/components/page-shell';
import { SupplierPicker } from '@/components/pickers/SupplierPicker';

/**
 * Supplier ledger / statement (route /reports/supplier-ledger), rendered to the
 * client format: a company + supplier header, opening balance, a chronological
 * DR (billed) / CR (paid) ledger with a running balance, and billed / paid /
 * net-payable totals. Pick a supplier + date range; search filters the rows.
 */

/** Rupees (number, signed) from a paise string, for spreadsheet math. */
const rupees = (paise: string): number => Number(paise) / 100;

const rowMatches = (r: SupplierLedgerRow, q: string): boolean => {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return [r.agentName, r.customerName, r.invoiceNumber, r.narration]
    .filter(Boolean)
    .some((v) => v!.toLowerCase().includes(t));
};

const refundMatches = (r: SupplierRefundRow, q: string): boolean => {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  return [r.invoiceNumber, r.supplierName, r.note, r.receivedReference]
    .filter(Boolean)
    .some((v) => v!.toLowerCase().includes(t));
};

export const SupplierLedgerPage = (): React.JSX.Element => {
  const fyState = useFyRange();
  const { from, to } = fyState;
  const [supplierId, setSupplierId] = useState<string>('');
  const [q, setQ] = useState('');
  const [receiving, setReceiving] = useState<SupplierRefundRow | null>(null);
  const [expandedRefund, setExpandedRefund] = useState<string | null>(null);

  const ledgerQuery = useSupplierLedger(supplierId || undefined, { from, to });
  const ledger = ledgerQuery.data;
  const allRows = ledger?.rows ?? [];
  const rows = useMemo(() => allRows.filter((r) => rowMatches(r, q)), [allRows, q]);

  const allRefunds = ledger?.pendingRefunds ?? [];
  const refunds = useMemo(() => allRefunds.filter((r) => refundMatches(r, q)), [allRefunds, q]);

  const handleExport = async (): Promise<void> => {
    if (!supplierId) return;
    try {
      const full = await getSupplierLedger(supplierId, { from, to });
      const header = [
        ['Company Name', full.company.name ?? ''],
        ['Address', full.company.address ?? ''],
        ['GSTIN', full.company.gstin ?? ''],
        [],
        ['Supplier Name', full.supplierName ?? '', '', 'From Date', full.fromDate ?? ''],
        ['Address', full.supplierAddress ?? '', '', 'To Date', full.toDate ?? ''],
        ['GSTIN', full.supplierGstin ?? '', '', 'Ledger Date', formatDateDMY(full.ledgerDate)],
        ['Opening Balance', rupees(full.openingBalancePaise)],
        ['Total Billed Amount', rupees(full.totalBilledPaise)],
        ['Paid Amount', rupees(full.totalPaidPaise)],
        ['Net Payable Amount', rupees(full.netPayablePaise)],
        ['Pending Refunds Receivable', rupees(full.pendingRefundsPaise ?? '0')],
        [],
        [
          'S.NO',
          'DATE',
          'AGENT NAME',
          'CUSTOMER NAME',
          'INVOICE NO',
          'NARRATION',
          'DR. AMT',
          'CR. AMT',
          'BALANCE',
        ],
        ['', '', '', '', '', 'OPENING BALANCE', '', '', rupees(full.openingBalancePaise)],
      ];
      const body = full.rows.map((r, i) => [
        i + 1,
        formatDateDMY(r.date),
        r.agentName ?? '-',
        r.customerName ?? '-',
        r.invoiceNumber ?? '-',
        r.narration ?? '',
        Number(r.drPaise) ? rupees(r.drPaise) : '',
        Number(r.crPaise) ? rupees(r.crPaise) : '',
        rupees(r.balancePaise),
      ]);
      const ws = XLSX.utils.aoa_to_sheet([...header, ...body]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Supplier Ledger');
      const refundRows = (full.pendingRefunds ?? []).map((r, i) => [
        i + 1,
        formatDateDMY(r.createdAt),
        r.invoiceNumber ?? '-',
        r.supplierName ?? '-',
        rupees(r.amountPaise),
        r.status,
        r.note ?? '',
      ]);
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([
          ['S.NO', 'DATE', 'INVOICE NO', 'SUPPLIER', 'REFUND AMT', 'STATUS', 'NOTE'],
          ...refundRows,
        ]),
        'Pending Refunds',
      );
      XLSX.writeFile(wb, `supplier-ledger-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      toast.error('Failed to export the ledger');
    }
  };

  return (
    <PageShell
      title="Supplier ledger"
      description="Statement for one supplier: opening balance, billed vs paid, and the running balance."
      actions={
        <UzButton
          variant="outline"
          leftIcon={<Download className="h-4 w-4" />}
          disabled={!supplierId || allRows.length === 0}
          onClick={() => void handleExport()}
        >
          Export Excel
        </UzButton>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <SupplierPicker
            label="Supplier"
            value={supplierId || null}
            onValueChange={(v) => setSupplierId(v ?? '')}
          />
        </div>
        <FyRangeControls state={fyState} />
        <UzInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search agent / customer / invoice / narration / refund..."
          containerClassName="w-72"
        />
      </div>

      {!supplierId ? (
        <UzCard padding="none">
          <p className="p-6 text-sm text-zinc-500">Select a supplier to view its statement.</p>
        </UzCard>
      ) : ledgerQuery.isLoading ? (
        <UzCard padding="none">
          <p className="p-6 text-sm text-zinc-500">Loading ledger...</p>
        </UzCard>
      ) : ledgerQuery.isError || !ledger ? (
        <UzCard padding="none">
          <p className="p-6 text-sm text-red-600">Could not load the ledger.</p>
        </UzCard>
      ) : (
        <>
          <UzCard className="mb-4">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div>
                <p className="text-[11px] font-semibold tracking-wider text-zinc-500 uppercase">
                  Company
                </p>
                <p className="mt-1 text-sm font-medium text-zinc-900">
                  {ledger.company.name ?? '-'}
                </p>
                <p className="text-sm text-zinc-600">{ledger.company.address ?? ''}</p>
                <p className="text-sm text-zinc-600">GSTIN: {ledger.company.gstin ?? '-'}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold tracking-wider text-zinc-500 uppercase">
                  Supplier
                </p>
                <p className="mt-1 text-sm font-medium text-zinc-900">
                  {ledger.supplierName ?? '-'}
                </p>
                <p className="text-sm text-zinc-600">{ledger.supplierAddress ?? ''}</p>
                <p className="text-sm text-zinc-600">GSTIN: {ledger.supplierGstin ?? '-'}</p>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-zinc-500">Opening balance</span>
                <span className="text-right tabular-nums">
                  {formatInrPaise(ledger.openingBalancePaise)}
                </span>
                <span className="text-zinc-500">Total billed</span>
                <span className="text-right tabular-nums">
                  {formatInrPaise(ledger.totalBilledPaise)}
                </span>
                <span className="text-zinc-500">Paid</span>
                <span className="text-right tabular-nums">
                  {formatInrPaise(ledger.totalPaidPaise)}
                </span>
                <span className="font-semibold text-zinc-900">Net payable</span>
                <span className="text-right font-semibold text-zinc-900 tabular-nums">
                  {formatInrPaise(ledger.netPayablePaise)}
                </span>
                <span className="text-zinc-500">Pending refunds (receivable)</span>
                <span className="text-right text-emerald-700 tabular-nums">
                  {formatInrPaise(ledger.pendingRefundsPaise ?? '0')}
                </span>
              </div>
            </div>
          </UzCard>

          <UzCard padding="none">
            <div className="overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                    <th className="px-3 py-3">S.No</th>
                    <th className="px-3 py-3">Date</th>
                    <th className="px-3 py-3">Agent</th>
                    <th className="px-3 py-3">Customer</th>
                    <th className="px-3 py-3">Invoice No.</th>
                    <th className="px-3 py-3">Narration</th>
                    <th className="px-3 py-3 text-right">Dr. Amt</th>
                    <th className="px-3 py-3 text-right">Cr. Amt</th>
                    <th className="px-3 py-3 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-zinc-100 bg-zinc-50/60 text-zinc-700">
                    <td className="px-3 py-3" colSpan={5} />
                    <td className="px-3 py-3 font-medium">Opening balance</td>
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3 text-right font-medium tabular-nums">
                      {formatInrPaise(ledger.openingBalancePaise)}
                    </td>
                  </tr>
                  {rows.map((r, i) => {
                    const isCredit = r.kind === 'PAYMENT';
                    return (
                      <tr
                        key={`${r.kind}-${r.date}-${i}`}
                        className="border-b border-zinc-100 last:border-0"
                      >
                        <td className="px-3 py-3 text-zinc-600 tabular-nums">{i + 1}</td>
                        <td className="px-3 py-3 text-zinc-700 tabular-nums">
                          {formatDateDMY(r.date)}
                        </td>
                        <td className="px-3 py-3 text-zinc-700">{r.agentName ?? '-'}</td>
                        <td className="px-3 py-3 text-zinc-700">{r.customerName ?? '-'}</td>
                        <td className="px-3 py-3 text-zinc-700 tabular-nums">
                          {r.invoiceNumber ?? '-'}
                        </td>
                        <td className="px-3 py-3 whitespace-normal text-zinc-700">
                          {r.narration ?? ''}
                        </td>
                        <td className="px-3 py-3 text-right text-zinc-700 tabular-nums">
                          {Number(r.drPaise) ? formatInrPaise(r.drPaise) : ''}
                        </td>
                        <td
                          className={`px-3 py-3 text-right tabular-nums ${isCredit ? 'text-emerald-700' : 'text-zinc-700'}`}
                        >
                          {Number(r.crPaise) ? formatInrPaise(r.crPaise) : ''}
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-zinc-900 tabular-nums">
                          {formatInrPaise(r.balancePaise)}
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td className="px-3 py-6 text-center text-sm text-zinc-500" colSpan={9}>
                        No transactions in this range.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="border-t border-zinc-200 bg-zinc-50 text-sm font-semibold text-zinc-900">
                    <td className="px-3 py-3" colSpan={6}>
                      Totals
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(ledger.totalBilledPaise)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(ledger.totalPaidPaise)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(ledger.netPayablePaise)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </UzCard>

          {allRefunds.length > 0 && (
            <UzCard
              className="mt-4"
              padding="none"
              title="Pending refunds"
              description="Refunds owed back by this supplier. Mark each as received once the money arrives."
            >
              <div className="overflow-x-auto">
                <table className="w-full text-sm whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                      <th className="w-8 px-3 py-3" />
                      <th className="px-3 py-3">Date</th>
                      <th className="px-3 py-3">Invoice No.</th>
                      <th className="px-3 py-3">Supplier</th>
                      <th className="px-3 py-3 text-right">Refund amt</th>
                      <th className="px-3 py-3">Status</th>
                      <th className="px-3 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {refunds.map((r) => {
                      const open = expandedRefund === r.id;
                      return (
                        <React.Fragment key={r.id}>
                          <tr className="border-b border-zinc-100">
                            <td className="px-3 py-3">
                              <button
                                type="button"
                                aria-label={open ? 'Hide detail' : 'Show detail'}
                                onClick={() => setExpandedRefund(open ? null : r.id)}
                              >
                                {open ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </button>
                            </td>
                            <td className="px-3 py-3 tabular-nums">{formatDateDMY(r.createdAt)}</td>
                            <td className="px-3 py-3 tabular-nums">{r.invoiceNumber ?? '-'}</td>
                            <td className="px-3 py-3">{r.supplierName ?? '-'}</td>
                            <td className="px-3 py-3 text-right tabular-nums">
                              {formatInrPaise(r.amountPaise)}
                            </td>
                            <td className="px-3 py-3">{r.status}</td>
                            <td className="px-3 py-3 text-right">
                              {r.status === 'PENDING' && (
                                <UzButton size="sm" onClick={() => setReceiving(r)}>
                                  Mark received
                                </UzButton>
                              )}
                            </td>
                          </tr>
                          {open && (
                            <tr className="border-b border-zinc-100 bg-zinc-50/60">
                              <td />
                              <td className="px-3 py-3 whitespace-normal text-zinc-600" colSpan={6}>
                                {r.note ?? 'No note.'}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {refunds.length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-center text-sm text-zinc-500" colSpan={7}>
                          No refunds match the search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </UzCard>
          )}
        </>
      )}
      <ReceiveRefundDialog
        refund={receiving}
        open={receiving !== null}
        onOpenChange={(o) => {
          if (!o) setReceiving(null);
        }}
      />
    </PageShell>
  );
};
